import { useTranslation } from 'react-i18next'

/** スタンプ/ポイント/両方の状態識別子 */
export type StampPointType = 'STAMP' | 'POINT' | 'STAMP_POINT'

/** useStampPointLabel の返り値 */
export type StampPointLabelResult = {
  /** 状態識別子。ボタンラベルや各種文言の切り替えに利用する */
  type: StampPointType
  /** i18n 対応済みの名詞ラベル（例: "スタンプ" / "ポイント" / "スタンプ・ポイント"） */
  label: string
}

/**
 * シート種別の有無フラグから StampPointType を解決する純粋関数。
 * @param hasStampSheets - stamp_rally / overlay / point_card シートが1件以上あるか
 * @param hasPointSheets - point_rally シートが1件以上あるか
 */
export function resolveStampPointType(hasStampSheets: boolean, hasPointSheets: boolean): StampPointType {
  if (hasStampSheets && hasPointSheets) return 'STAMP_POINT'
  if (hasPointSheets) return 'POINT'
  return 'STAMP'
}

/**
 * sheetTypes 配列からスタンプ/ポイント判定フラグを導出する純粋関数。
 * 判定ロジックを一か所に集約し、呼び出し元での重複を防ぐ。
 * @param sheetTypes - キャンペーン/スポット等の sheetTypes 配列
 */
export function resolveStampPointFlagsFromSheetTypes(sheetTypes: string[]): {
  hasStampSheets: boolean
  hasPointSheets: boolean
} {
  return {
    hasStampSheets: sheetTypes.some((st) => st === 'stamp_rally' || st === 'overlay' || st === 'point_card'),
    hasPointSheets: sheetTypes.some((st) => st === 'point_rally'),
  }
}

/**
 * シート種別フラグから StampPointType と i18n ラベルを返すフック。
 * @param hasStampSheets - stamp_rally / overlay / point_card シートが1件以上あるか
 * @param hasPointSheets - point_rally シートが1件以上あるか
 */
export function useStampPointLabel(hasStampSheets: boolean, hasPointSheets: boolean): StampPointLabelResult {
  const { t } = useTranslation()

  const type = resolveStampPointType(hasStampSheets, hasPointSheets)

  const label =
    type === 'POINT'
      ? t('common.point', 'ポイント')
      : type === 'STAMP_POINT'
        ? t('common.stamp_and_point', 'スタンプ・ポイント')
        : t('common.stamp', 'スタンプ')

  return { type, label }
}

/**
 * sheetTypes 配列から直接 StampPointType と i18n ラベルを返すフック。
 * `resolveStampPointFlagsFromSheetTypes` + `useStampPointLabel` の組み合わせを省略できる。
 * @param sheetTypes - キャンペーン/スポット等の sheetTypes 配列
 */
export function useStampPointLabelFromSheetTypes(sheetTypes: string[]): StampPointLabelResult {
  const { hasStampSheets, hasPointSheets } = resolveStampPointFlagsFromSheetTypes(sheetTypes)
  return useStampPointLabel(hasStampSheets, hasPointSheets)
}
